import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { addToCart, changeCartLine, visibleSectionIds } from '@theme/cart-utils';
import { getGiftLineProperties } from '@theme/line-item-properties';
import { formatMoney } from '@theme/money-formatting';
import { SsPrintSwatches } from '@theme/ss-print-swatches';
import { FreeGiftVariantSelector } from '@theme/free-gift-variant-selector';

/** One print in the selector, matching `ss-print-swatches.js`. */
const PRINT_SWATCH_SELECTOR = 'a.product-swatches__swatch';

/**
 * The product handle in a product path, which is its last segment.
 *
 * @param {string} url
 * @returns {string}
 */
const getHandleFromUrl = (url) =>
  String(url ?? '')
    .split('?')[0]
    ?.split('/')
    .filter(Boolean)
    .pop() ?? '';

/**
 * The cadence the offer's plan delivers on, hardcoded because the interval itself is out of reach:
 * `sellingPlan.deliveryPolicy` holds it, and it is a union in the Storefront API that a web
 * component's query cannot open a fragment on. The offer runs on the sock subscription, which is
 * monthly.
 */
const OFFER_PLAN_CADENCE = 'monthly';

/**
 * Get the selling plan the offer adds on, as `{ id, cents, cadence }`. Read from `shopifyData`
 * rather than fetched.
 *
 * Which plan it is comes from a setting, because a product can carry a monthly plan from more than
 * one subscription app and nothing in the data says which of them the offer means — the sock
 * products hold both "Delivered every month" and a second app's "Delivered every 1 month". The name
 * is matched whole, since neither of those contains the other, and an unmatched name resolves to
 * nothing rather than to whichever plan the product happened to report first.
 *
 * No name at all is not a failure: the gift is then added outright, with no plan. The headless
 * storefront offered the same choice (components/promotions/FreeItemCartDetail.js only looks a plan
 * up behind its `isSubscription` flag), so the caller decides what a missing plan means.
 *
 * @param {any} data - `shopifyData` from the product context.
 * @param {string} planName - The configured plan name, compared case-insensitively. Empty resolves
 *   to null, for a gift that is not a subscription.
 * @returns {{ id: string, cents: number, cadence: string } | null}
 */
function getOfferPlan(data, planName) {
  const wanted = planName.trim().toLowerCase();
  if (!wanted) return null;

  const nodes = data?.selectedOrFirstAvailableVariant?.sellingPlanAllocations?.nodes ?? [];

  for (const node of nodes) {
    const plan = node?.sellingPlan;
    const amount = node?.priceAdjustments?.[0]?.price?.amount;
    if (!plan?.id || amount == null) continue;

    const name = String(plan.name ?? '')
      .trim()
      .toLowerCase();
    if (name !== wanted) continue;

    return {
      // a GID; the Ajax cart wants the number
      id: plan.id.split('/').pop() ?? '',
      cents: Math.round(Number.parseFloat(amount) * 100),
      cadence: OFFER_PLAN_CADENCE,
    };
  }

  return null;
}

/**
 * The free gift offer's behaviour, over whichever base the layout needs — `DialogComponent` for the
 * panel, plain `Component` for the card. A mixin rather than a base class because those two bases
 * are siblings.
 *
 * The contents are Storefront Web Components, so switching print or cut costs no request; this
 * covers what they do not — the cart line, swapping an existing gift line, the size guide and the
 * terms copy. `shopify-cart` is deliberately unused: it keeps its own Storefront API cart, not the
 * session cart cart-items-component renders.
 *
 * @typedef {object} OfferRefs
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [submitLabel]
 * @property {HTMLElement} [error]
 * @property {HTMLElement} [context] - The product context whose print is on screen
 * @property {FreeGiftVariantSelector} [sizes] - The size buttons, which own the variant choice
 * @property {HTMLElement} [price] - Where the renewal price lands in the terms line
 * @property {SsPrintSwatches} [prints] - The print selector
 * @property {DialogComponent} [sizeGuideDialog] - The drawer the size guide sits in
 *
 * @template {new (...args: any[]) => Component} TBase
 * @param {TBase} Base
 */
const FreeGiftOffer = (Base) =>
  class extends Base {
    /**
     * Narrows `this.refs`, which neither base is generic over. Same cast cart-drawer.js uses.
     *
     * @returns {OfferRefs}
     */
    get elements() {
      return /** @type {OfferRefs} */ (/** @type {unknown} */ (this.refs));
    }

    /** The configured add label, kept so edit mode can swap it and swap it back. */
    #addLabel = '';

    #submitting = false;

    /** Money-formatted renewal price for the cart's offer row. */
    #offerPrice = '';

    #offerResolved = false;

    connectedCallback() {
      super.connectedCallback();

      // `free-gift-slot` moving the card re-runs this, and by then the label may read "Update"
      this.#addLabel ||= this.elements.submitLabel?.textContent?.trim() ?? '';

      // a cart morph replaces the filled offer row with the server's empty one, and takes the gift's
      // edit control with it when the shopper removes the line
      document.addEventListener(ThemeEvents.cartUpdate, this.#fillOfferTerms);
      document.addEventListener(ThemeEvents.cartUpdate, this.#setSubmitLabel);

      // the context's own render, after it has the data — unlike `data-loaded`, which the store fires
      // before assigning it, and not at all for a handle it already holds
      this.addEventListener('shopify-render', this.#resolveOfferPrice);
      this.addEventListener('shopify-render', this.#carrySize);

      this.#slideObserver.observe(this, { childList: true, subtree: true });
      this.addEventListener(SsPrintSwatches.swatchUpdate, this.#handlePrintChange);
      this.addEventListener(SsPrintSwatches.swatchInit, this.#handlePrintChange);
      this.addEventListener(FreeGiftVariantSelector.changed, this.#handleSizeChange);
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      document.removeEventListener(ThemeEvents.cartUpdate, this.#fillOfferTerms);
      document.removeEventListener(ThemeEvents.cartUpdate, this.#setSubmitLabel);
      this.removeEventListener('shopify-render', this.#resolveOfferPrice);
      this.removeEventListener('shopify-render', this.#carrySize);
      this.#slideObserver.disconnect();
      this.removeEventListener(SsPrintSwatches.swatchUpdate, this.#handlePrintChange);
      this.removeEventListener(SsPrintSwatches.swatchInit, this.#handlePrintChange);
      this.removeEventListener(FreeGiftVariantSelector.changed, this.#handleSizeChange);
    }

    /**
     * Reconnect the gallery slideshow once the context has cloned its slides in: the component
     * counts them in `connectedCallback`, and the context renders the images after that runs.
     */
    #slideObserver = new MutationObserver(() => {
      const slideshow = this.querySelector('slideshow-component');
      const parent = slideshow?.parentElement;
      if (!slideshow || !parent || !slideshow.querySelector('slideshow-slide')) return;
      if (slideshow.hasAttribute('data-slides-ready')) return;

      slideshow.setAttribute('data-slides-ready', '');
      slideshow.remove();
      parent.prepend(slideshow);
    });

    /** The size label carried onto each new print, empty until the shopper picks one. */
    #pickedSize = '';

    /** @returns {string} The picked size's variant id, empty until a size is picked. */
    get #variantId() {
      return this.#sizes?.variantId ?? '';
    }

    /**
     * The gift's cart line, read from the control the cart renders beside it rather than remembered
     * from the click that opened the offer. `free-gift-slot` skips its subtree on morph, so the offer
     * outlives every cart it was opened against — a key kept in a field goes on naming a line the
     * shopper has since removed, and the next submit adds a second gift instead of swapping the first.
     *
     * @returns {string} Empty when the gift is not in the cart, so the offer is adding.
     */
    get #giftLineKey() {
      const edit = /** @type {HTMLElement | null} */ (document.querySelector('free-gift-trigger [data-line-key]'));

      return edit?.dataset.lineKey ?? '';
    }

    /**
     * Reads the renewal price off the context's own data. Left unresolved when there is nothing to read
     * yet, so a later render tries again.
     */
    #resolveOfferPrice = () => {
      if (this.#offerResolved) return;

      const plan = getOfferPlan(this.#data, this.#planName);
      if (!plan) return;

      this.#offerPrice = this.#money(plan.cents);
      this.#offerResolved = true;
      this.#fillOfferTerms();
    };

    /**
     * Write the renewal price into every terms line: the cart's offer row from its own `data-template`,
     * so repeated fills cannot compound, and this layout's own into a `price` ref.
     */
    #fillOfferTerms = () => {
      if (!this.#offerResolved) return;

      const write = () => {
        for (const target of document.querySelectorAll('[data-free-gift-terms]')) {
          if (!(target instanceof HTMLElement)) continue;

          const template = target.dataset.template;
          if (!template) continue;

          // empty is the skeleton state, so no price hides the line
          target.hidden = !this.#offerPrice;
          target.textContent = this.#offerPrice ? template.replace('[price]', this.#offerPrice) : '';
        }

        // this layout's own line keeps its markup and takes the price in a span
        if (this.refs.price) this.refs.price.textContent = this.#offerPrice ?? '';
      };

      // twice: a sync morph is already swapped, an async section render is not
      write();
      requestAnimationFrame(write);
    };

    /**
     * Runs the print selector's first query and writes the renewal price into the terms, so a
     * shopper who never opens the offer still sees one. Called by `free-gift-trigger` with the
     * on-screen control's dataset — an edit trigger names a cart line's gift, so that loads instead.
     *
     * @param {DOMStringMap | Record<string, string | undefined>} [config]
     */
    hydrate(config = {}) {
      this.#setCutAndPrint(config);

      const prints = this.elements.prints;
      if (prints && !prints.searchspringData) prints.pending = prints.fetchData();

      this.#fillOfferTerms();
    }

    /**
     * Sets which cut and print the selector shows: the cut's collection and product type, and the print
     * to mark. The cut's own option carries the collection, so that mapping lives in the blocks alone.
     *
     * @param {DOMStringMap | Record<string, string | undefined>} config - `cut` and `productId` as a
     *   trigger carries them.
     */
    #setCutAndPrint(config) {
      const prints = this.elements.prints;
      if (!prints) return;

      const option = config.cut ? this.#checkOption(config.cut) : null;

      this.#setPrintCollection(option?.dataset.collection);
      if (config.cut) prints.dataset.productType = config.cut;
      if (config.productId) prints.dataset.productId = config.productId;
    }

    /** Remembers the size, to carry onto the next print, and clears the "pick a size" error. */
    #handleSizeChange = () => {
      this.#pickedSize = this.#sizes?.selectedSize ?? '';
      this.#showError('');
    };

    /**
     * Keeps the shopper's size across prints and cuts: each render is a different product, so the size is
     * carried by its label and re-picked only where that size is in stock.
     */
    #carrySize = () => {
      this.#sizes?.restore(this.#pickedSize);
    };

    /**
     * The size buttons. A render replaces them, so a stale ref is stepped over rather than waited on —
     * `refs` is refreshed by an observer registered in `requestIdleCallback`.
     *
     * @returns {FreeGiftVariantSelector | null}
     */
    get #sizes() {
      const sizes = this.elements.sizes;
      if (sizes?.isConnected) return sizes;

      return /** @type {FreeGiftVariantSelector | null} */ (this.querySelector('free-gift-variant-selector'));
    }

    /**
     * Shows the picked print, and the one the selector settled on when it first rendered.
     *
     * @param {Event} event
     */
    #handlePrintChange = (event) => {
      const handle = getHandleFromUrl(/** @type {CustomEvent} */ (event).detail?.url);
      if (!handle) return;

      this.#setShopifyContextHandle(handle);
      this.#setSizeGuideHandle(handle);
    };

    /**
     * Pre-populates the offer from a trigger's `data-cut` and `data-product-id`. `reveal` is what
     * each layout does with that.
     *
     * @param {Event} [event]
     */
    open(event) {
      const trigger = event?.target;
      const config = trigger instanceof HTMLElement ? trigger.dataset : {};

      this.#setSubmitLabel();
      this.#showError('');

      // editing opens on the line's own gift, and the selector announces it back
      this.#setCutAndPrint(config);

      this.reveal();
    }

    /** Brings the offer into view. Overridden per layout. */
    reveal() {}

    /** Runs once a line has been added. Overridden per layout. */
    async afterAdd() {}

    /** Opens the size guide drawer. Its guide was fetched when the print was set. */
    openSizeGuide() {
      this.elements.sizeGuideDialog?.showDialog();
    }

    /**
     * Each cut is its own product type. Only the selector is re-pointed: it re-queries and marks the
     * print last picked in that cut, but a re-query is a filter and announces nothing, so the offer
     * asks it to. A cut not visited yet marks nothing, so the first print stands in.
     *
     * @param {Event} event
     */
    async selectProductType(event) {
      const checked = event.target;
      if (!(checked instanceof HTMLInputElement)) return;

      const prints = this.elements.prints;
      if (!prints) return;

      // the attribute is what queries; this only waits for that query
      this.#setPrintCollection(checked.dataset.collection);
      prints.dataset.productType = checked.value;
      await prints.pending;

      if (prints.querySelector(`${PRINT_SWATCH_SELECTOR}[aria-current]`)) {
        prints.emitInitialSelection();
      } else {
        /** @type {HTMLElement | null} */ (prints.querySelector(PRINT_SWATCH_SELECTOR))?.click();
      }
    }

    /** @param {Event} [event] */
    async add(event) {
      event?.preventDefault();

      // claimed before the first await: the offer stays on screen, so a second tap would add a line
      if (this.#submitting) return;
      this.#submitting = true;
      this.#setBusy(true);

      try {
        const variantId = this.#variantId;

        if (!variantId) {
          this.#showError(Theme.translations.free_gift_select_size);
          return;
        }

        if (!this.#sizes?.available) {
          this.#showError(Theme.translations.free_gift_unavailable);
          return;
        }

        // A plan the merchant named but this product does not carry is a misconfiguration, and adding
        // outright would give away a subscription's worth of socks. No name is the other case: the
        // offer is a one-time gift, and `addToCart` leaves `selling_plan` off the request.
        const planName = this.#planName;
        const plan = getOfferPlan(this.#data, planName);
        if (planName && !plan) {
          this.#showError(Theme.translations.free_gift_unavailable);
          return;
        }

        // read before the add, while the cart on screen is still the one being swapped out of
        const editLineKey = this.#giftLineKey;
        const sections = visibleSectionIds();
        const response = await addToCart(variantId, {
          sellingPlanId: plan?.id,
          properties: getGiftLineProperties(plan),
          sections,
        });

        if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

        let cart = await response.json();
        let staleLineRemains = false;

        // add before removing, so a failure leaves the existing line untouched
        if (editLineKey) {
          try {
            const changed = await changeCartLine(editLineKey, { quantity: 0, sections });
            if (!changed.ok) throw new Error(`Cart change failed: ${changed.status}`);

            cart = await changed.json();
          } catch (error) {
            console.error(error);
            staleLineRemains = true;
          }
        }

        const cartEvent = new CartAddEvent(cart, 'free-gift-offer', {
          source: 'free-gift-offer',
          itemCount: cart.item_count,
          variantId,
          sections: cart.sections,
        });

        // re-rendered either way; a failed swap keeps the offer on screen rather than hide a duplicate
        if (staleLineRemains) {
          document.dispatchEvent(cartEvent);
          this.#showError(Theme.translations.free_gift_duplicate);
          return;
        }

        await this.afterAdd();
        document.dispatchEvent(cartEvent);
      } catch (error) {
        console.error(error);
        this.#showError(Theme.translations.free_gift_error);
      } finally {
        this.#submitting = false;
        this.#setBusy(false);
      }
    }

    /**
     * Re-points the context at a product, by handle — the only value its `update()` re-queries with. It
     * reads `handle` off the source context it resolves from the event's target, so a `gid` set here
     * would render once and never change.
     *
     * @param {string} handle
     */
    #setShopifyContextHandle(handle) {
      const context = /** @type {any} */ (this.elements.context);
      if (!handle || !context || context.getAttribute('handle') === handle) return;

      context.setAttribute('handle', handle);

      // `update()` reads only `.target`, resolving it to the nearest context — here, itself. Setting the
      // attribute alone appends a second copy; with `wait-for-update` it renders nothing at all.
      if (typeof context.update === 'function') context.update({ target: context });
    }

    /**
     * Points the size guide at the print on screen; it fetches its own markup. `querySelector`, not
     * a ref: it sits inside its own `dialog-component`, and refs stop at the nearest component.
     *
     * @param {string} handle
     */
    #setSizeGuideHandle(handle) {
      const guide = /** @type {HTMLElement | null} */ (this.querySelector('size-guide-tabs'));
      if (guide) guide.dataset.handle = handle;
    }

    /**
     * The plan name the merchant configured, empty for a gift that is not a subscription.
     *
     * @returns {string}
     */
    get #planName() {
      return this.dataset.sellingPlanName ?? '';
    }

    /** The product object the detail context has resolved, or null before it loads. */
    get #data() {
      return /** @type {any} */ (this.elements.context)?.shopifyData ?? null;
    }

    /**
     * @param {number} cents
     * @returns {string} The amount in the shop's own money format.
     */
    #money(cents) {
      return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || '');
    }

    /** @param {boolean} busy */
    #setBusy(busy) {
      const { submit } = this.elements;
      if (!submit) return;

      // `aria-disabled`, not `disabled`: a disabled button drops out of the tab order mid-submit
      submit.setAttribute('aria-disabled', String(busy));
      if (busy) submit.setAttribute('aria-busy', 'true');
      else submit.removeAttribute('aria-busy');
    }

    /**
     * Whether the button adds the gift or swaps the one in the cart. Written twice for the same
     * reason the terms are: a sync morph is already swapped, an async section render is not.
     */
    #setSubmitLabel = () => {
      const write = () => {
        const { submitLabel } = this.elements;
        if (!submitLabel) return;

        submitLabel.textContent = this.#giftLineKey ? (Theme.translations.free_gift_update ?? '') : this.#addLabel;
      };

      write();
      requestAnimationFrame(write);
    };

    /**
     * Selects the option matching a product type, compared case-insensitively: an option's value is
     * the type as Shopify reports it on the configured product, while the Edit Item trigger sends
     * the cart line's own `product.type`.
     *
     * @param {string} type
     * @returns {HTMLInputElement | null} The option, which also carries the cut's collection.
     */
    #checkOption(type) {
      const wanted = type.trim().toLowerCase();

      for (const input of this.querySelectorAll('input[name="free-gift-option"]')) {
        if (!(input instanceof HTMLInputElement) || input.value.trim().toLowerCase() !== wanted) continue;

        input.checked = true;
        return input;
      }

      return null;
    }

    /**
     * Points the selector at a cut's curated collection, the prints the offer is drawn from. Written
     * before `data-product-type`, since that is the attribute whose change refetches.
     *
     * @param {string} [collection]
     */
    #setPrintCollection(collection) {
      const prints = this.elements.prints;
      if (!prints || !collection) return;

      const bgfilters = new URLSearchParams(prints.dataset.bgfilters ?? '');
      bgfilters.set('collection_handle', collection);
      prints.dataset.bgfilters = bgfilters.toString();
    }

    /**
     * Errors are announced by mutating a region that is always in the accessible tree — a `hidden`
     * element unhidden after its text is set is frequently not announced.
     *
     * @param {string} [message]
     */
    #showError(message) {
      if (this.elements.error) this.elements.error.textContent = message ?? '';
    }
  };

/** The offer as a drawer that stacks over the cart, opened from a row in it. */
class FreeGiftQuickAdd extends FreeGiftOffer(DialogComponent) {
  reveal() {
    this.showDialog();
  }

  async afterAdd() {
    await this.closeDialog();
  }
}

/**
 * The offer as a card inside the cart. Shown in place while the gift is still on offer; once it is
 * taken the cart shows the line instead, and `editing` is what brings the card back — see the
 * `.cart-drawer__free-gift` rules in snippets/header-actions.liquid.
 */
class FreeGiftCard extends FreeGiftOffer(Component) {
  reveal() {
    this.toggleAttribute('editing', true);
    this.scrollIntoView({ block: 'nearest' });
  }

  async afterAdd() {
    this.removeAttribute('editing');

    // The slot's state is the server's answer and the cart section has not re-rendered yet, so the
    // card would sit there clickable from the moment the spinner clears until the morph lands — long
    // enough to add a second gift. This writes the answer the add already produced. Nothing has to
    // clear it: the morph overwrites the attribute on every cart update, so removing the gift brings
    // the card back on the server's say-so rather than on a flag kept here.
    const slot = this.closest('free-gift-slot');
    if (slot instanceof HTMLElement) slot.dataset.state = 'added';
  }
}

if (!customElements.get('free-gift-quick-add')) {
  customElements.define('free-gift-quick-add', FreeGiftQuickAdd);
}

if (!customElements.get('free-gift-card')) {
  customElements.define('free-gift-card', FreeGiftCard);
}

/**
 * Wraps a control that opens the offer — or, in the card layout, the card itself — and hydrates it
 * while that control is on screen, so opening it finds the prints already there.
 *
 * Kept observing rather than disconnecting on the first hit, because a reopened drawer needs its
 * price written into the server's fresh copy of the offer row. The wrapper is observed, not the
 * control inside it: a cart morph inserts this element before its children.
 */
class FreeGiftTrigger extends HTMLElement {
  #inView = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    const offer = /** @type {any} */ (document.getElementById('free-gift-offer'));
    if (typeof offer?.hydrate === 'function') offer.hydrate(this.firstElementChild?.dataset ?? {});
  });

  connectedCallback() {
    this.#inView.observe(this);
  }

  disconnectedCallback() {
    this.#inView.disconnect();
  }
}

if (!customElements.get('free-gift-trigger')) {
  customElements.define('free-gift-trigger', FreeGiftTrigger);
}

/**
 * Where the cart wants the offer card, which is not where the card can be rendered: it belongs to
 * `sections/free-gift-offer.liquid`, which owns the option blocks, while the drawer is markup of
 * the header section. So the card is rendered at the end of the page and moved here.
 *
 * The slot carries `data-skip-subtree-update`, or a cart morph would delete a card the server never
 * rendered inside it. When the gift is taken the slot goes with it, and the card goes home.
 */
class FreeGiftSlot extends HTMLElement {
  /**
   * Where the card was rendered, so it has somewhere to go back to.
   *
   * @type {HTMLElement | null}
   */
  #home = null;

  connectedCallback() {
    // The header parses before the section that renders the card, so the first claim waits.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#claim, { once: true });
    } else {
      this.#claim();
    }
  }

  disconnectedCallback() {
    const host = this.firstElementChild;
    if (host && this.#home) this.#home.appendChild(host);
  }

  #claim = () => {
    if (!this.isConnected || this.firstElementChild) return;

    const host = document.querySelector('free-gift-card')?.parentElement;
    if (!host || host.parentElement === this) return;

    this.#home = host.parentElement;
    this.appendChild(host);
  };
}

if (!customElements.get('free-gift-slot')) {
  customElements.define('free-gift-slot', FreeGiftSlot);
}
